/**
 * Geometry for the software digital-zoom decode pass.
 *
 * Budget Android rear cameras that cannot focus close enough to fill the frame
 * with a small product barcode still deliver a sharp image when the operator
 * holds the phone back — the bars are simply too few pixels wide for the
 * decoder. Cropping what the operator framed and drawing it into a canvas with
 * more samples per bar replaces the zoom range the hardware never exposed.
 *
 * Two rules keep that affordable on the weak phones this exists for:
 *  - upscale only while the crop is narrower than the target width. A 1440p
 *    feed already hands over a wide crop; doubling it costs 4x the pixels for
 *    samples the decoder does not need.
 *  - never exceed the output budget. One tick allocates dWidth*dHeight*4 bytes
 *    of ImageData several times a second.
 */

/** Source rectangle to read from the video and the canvas size to draw into. */
export interface RoiCrop {
    sx: number
    sy: number
    sWidth: number
    sHeight: number
    dWidth: number
    dHeight: number
}

/** Bars below this crop width are worth resampling; above it they are not. */
const TARGET_CROP_WIDTH = 500
/** Ceiling on one decode pass: ~4 MB of ImageData per tick. */
const MAX_OUTPUT_PIXELS = 1_000_000
/** Above 4x the canvas costs more than the extra samples buy on a weak phone. */
const MAX_UPSCALE = 4

function requireFinite(value: number, name: string): void {
    if (!Number.isFinite(value)) {
        throw new RangeError(`${name} must be a finite number, got ${value}`)
    }
}

/**
 * Centre `sWidth`x`sHeight` inside the frame and size the canvas for it.
 * `maxUpscale` is a ceiling, not a demand: the effective factor comes from how
 * narrow the crop is, and the result is trimmed to the output budget even when
 * that means drawing the crop smaller than it was read.
 */
function centredCrop(
    videoW: number,
    videoH: number,
    sWidth: number,
    sHeight: number,
    maxUpscale: number,
): RoiCrop {
    const ceiling = Math.min(MAX_UPSCALE, Math.max(1, maxUpscale))
    const upscale = Math.min(ceiling, Math.max(1, TARGET_CROP_WIDTH / sWidth))
    let dWidth = Math.max(1, Math.round(sWidth * upscale))
    let dHeight = Math.max(1, Math.round(sHeight * upscale))

    if (dWidth * dHeight > MAX_OUTPUT_PIXELS) {
        // Floor, so rounding can never push the canvas back over the budget.
        const trim = Math.sqrt(MAX_OUTPUT_PIXELS / (dWidth * dHeight))
        dWidth = Math.max(1, Math.floor(dWidth * trim))
        dHeight = Math.max(1, Math.floor(dHeight * trim))
    }

    return {
        // Floor the offsets so the rect stays inside the frame even when
        // rounding pushed the box one pixel wider.
        sx: Math.floor((videoW - sWidth) / 2),
        sy: Math.floor((videoH - sHeight) / 2),
        sWidth,
        sHeight,
        dWidth,
        dHeight,
    }
}

/**
 * Fallback geometry: a centred box scaled by `roiScale` on both axes, used
 * when the rendered aim box cannot be measured.
 *
 * `roiScale` is clamped to (0, 1] and `maxUpscale` to [1, 4]; both are tuning
 * parameters, so out-of-range input degrades to the nearest sane pass instead
 * of failing a scan. Frame dimensions are different: a zero or NaN size means
 * the video has no pixels yet and the caller must skip the frame, so that
 * throws.
 */
export function computeRoiCrop(
    videoW: number,
    videoH: number,
    roiScale: number,
    maxUpscale: number,
): RoiCrop {
    requireFinite(videoW, 'videoW')
    requireFinite(videoH, 'videoH')
    requireFinite(roiScale, 'roiScale')
    requireFinite(maxUpscale, 'maxUpscale')
    if (videoW <= 0 || videoH <= 0) {
        throw new RangeError(
            `video frame has no pixels (${videoW}x${videoH}) — skip the frame`,
        )
    }

    const scale = Math.min(1, Math.max(Number.EPSILON, roiScale))
    return centredCrop(
        videoW,
        videoH,
        Math.min(videoW, Math.max(1, Math.round(videoW * scale))),
        Math.min(videoH, Math.max(1, Math.round(videoH * scale))),
        maxUpscale,
    )
}

export interface AimRoiInput {
    /** Rendered preview stage, CSS pixels. */
    stageW: number
    stageH: number
    /** Rendered aiming frame inside that stage, CSS pixels. */
    aimW: number
    aimH: number
    /** Intrinsic video size. */
    videoW: number
    videoH: number
    maxUpscale: number
}

/**
 * Crop exactly what the operator sees inside the aiming frame.
 *
 * The preview paints the feed with object-cover, which scales it by
 * max(stageW/videoW, stageH/videoH) and clips the overflow, so one CSS pixel
 * of the aim box is 1/cover video pixels. Deriving the crop from the measured
 * layout keeps it correct across rotation, tablet widths and the 55vh cap,
 * where a fixed fraction silently cuts the ends off a framed barcode.
 *
 * Returns null when any measurement is missing or zero — the caller falls back
 * to the fixed fraction rather than guessing.
 */
export function computeAimRoiCrop(input: AimRoiInput): RoiCrop | null {
    const { stageW, stageH, aimW, aimH, videoW, videoH } = input
    const measurements = [stageW, stageH, aimW, aimH, videoW, videoH]
    if (measurements.some((value) => !Number.isFinite(value) || value <= 0)) {
        return null
    }
    if (!Number.isFinite(input.maxUpscale)) return null

    const cover = Math.max(stageW / videoW, stageH / videoH)
    return centredCrop(
        videoW,
        videoH,
        Math.min(videoW, Math.max(1, Math.round(aimW / cover))),
        Math.min(videoH, Math.max(1, Math.round(aimH / cover))),
        input.maxUpscale,
    )
}
